import {
  Breakpoints,
  Button,
  ButtonColor,
  ButtonSize,
  formatName,
  IconLink,
  pluralize,
  ProfileImage,
  Rating,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import React, { useMemo } from 'react';

import {
  ExperienceType,
  GoalName,
  type Scalars,
} from '../../__generated-gql-types__/globalTypes';
import { getCoachUrl } from '../../utils/coach';
import { APPLICANT_SITE_URL } from '../../utils/constants';
import useCopyToClipboardWithToast from '../../utils/hooks/useCopyToClipboardWithToast';
import { TierBadge } from '../TierBadge';
import Breadcrumbs from '../breadcrumbs/Breadcrumbs';
import { useCategoryGoalBreadcrumbs } from '../breadcrumbs/useCategoryGoalBreadcrumbs';

import AdditionalExperienceTag from './AdditionalExperienceTag';
import CompanyTag from './CompanyTag';
import SchoolTag from './SchoolTag';
import { SuperCoachTag } from './SuperCoachTag';
import type { CoachInfoCoachFragment } from './__generated-gql-types__/CoachInfo.generated';

interface CoachInfoProps {
  coach: CoachInfoCoachFragment;
  categoryId: Nullable<Scalars['CategoryUrn']['output']>;
  onReviewsClick: () => void;
}

const CoachInfo: React.FC<CoachInfoProps> = ({
  coach,
  categoryId,
  onReviewsClick,
}) => {
  const isSm = useMediaQuery(Breakpoints.SM);
  const copyToClipboard = useCopyToClipboardWithToast();

  const shouldShowCoachTier = false;

  const selectedCategory = useMemo(
    () =>
      coach.categoryInformationList?.find(
        (categoryInfo) => categoryInfo.category.id == categoryId,
      ),
    [coach.categoryInformationList, categoryId],
  );

  const hasEducationAdmissionsCommitteeExperience =
    coach.educationExperiences?.some(
      (education) => education.admissionCommittee,
    );
  const hasProfessionalAdmissionsCommitteeExperience =
    coach.professionalExperiences?.some(
      (experience) =>
        experience.experienceType === ExperienceType.ADMISSIONS_COMMITTEE,
    );
  const hasProfessionalHiringExperience = coach.professionalExperiences?.some(
    (experience) => experience.experienceType === ExperienceType.HIRING_MANAGER,
  );

  const formattedCoachName = formatName(coach.user);

  const breadcrumbs = useCategoryGoalBreadcrumbs({
    category: selectedCategory?.category,
  });

  const COPY_LINK_TO_PROFILE_BUTTON = isSm ? (
    <Button
      size={ButtonSize.SMALL}
      buttonColor={ButtonColor.WHITE}
      rounded
      LeftIcon={IconLink}
      label="Copy link to profile"
      id="coach_info:copy_link"
      onClick={() =>
        copyToClipboard(
          `${APPLICANT_SITE_URL}${getCoachUrl({
            coachSlug: coach.slug,
            categorySlug: selectedCategory?.category.slug,
          })}`,
          'profile link',
        )
      }
    />
  ) : null;

  return (
    <>
      {breadcrumbs.length > 0 ? (
        <div className="mb-4 flex w-full justify-between">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          {COPY_LINK_TO_PROFILE_BUTTON}
        </div>
      ) : null}
      <div className="flex w-full flex-col items-center md:flex-row">
        <ProfileImage
          className="h-25 w-25"
          pictureLink={coach.user.pictureLink}
          firstName={coach.user.firstName}
          lastName={coach.user.lastName}
        />
        <div className="ml-0 mt-6 flex w-full flex-col items-center text-center md:ml-8 md:mt-0 md:items-start md:text-left">
          <div
            className={`w-full text-lg font-medium leading-none ${
              selectedCategory
                ? 'flex flex-wrap items-center justify-center space-x-2 md:justify-start'
                : 'flex flex-col items-center space-y-1 md:items-start'
            }`}
          >
            <div
              className={`flex justify-center space-x-2 md:justify-between ${
                selectedCategory ? '' : 'w-full'
              }`}
            >
              <p
                className={`whitespace-nowrap font-medium text-leland-gray-dark ${
                  selectedCategory ? 'text-lg' : 'text-2xl'
                }`}
              >
                {formattedCoachName}
              </p>
              {selectedCategory ? null : COPY_LINK_TO_PROFILE_BUTTON}
            </div>
            <div className="flex items-center space-x-2">
              {coach.averageReviewRate > 0 ? (
                <>
                  {selectedCategory ? (
                    <div className="size-0.5 rounded bg-leland-gray-light" />
                  ) : null}
                  <Rating
                    totalStars={1}
                    rate={coach.averageReviewRate}
                    showRate
                  />
                  <button
                    type="button"
                    className="whitespace-nowrap text-base font-medium text-leland-primary underline"
                    onClick={onReviewsClick}
                    id="coach_info:view_reviews"
                  >
                    {pluralize(coach.reviewTotal, 'review')}
                  </button>
                </>
              ) : null}
              {shouldShowCoachTier ? (
                <>
                  <div className="size-0.5 rounded bg-leland-gray-light" />
                  <TierBadge
                    tooltipSide="bottom"
                    tier={coach.tier}
                    goal={selectedCategory?.category.goal}
                  />
                </>
              ) : null}
            </div>
          </div>
          {selectedCategory?.headline ? (
            <h2 className="mt-1 text-2xl font-medium text-leland-gray-dark">
              {selectedCategory.headline}
            </h2>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center justify-start gap-2">
            {coach.isSuperCoach ? <SuperCoachTag /> : null}
            <SchoolTag coach={coach} />
            <CompanyTag coach={coach} />
            {selectedCategory?.category.goal === GoalName.SCHOOL &&
            (hasEducationAdmissionsCommitteeExperience ||
              hasProfessionalAdmissionsCommitteeExperience) ? (
              <AdditionalExperienceTag
                type={ExperienceType.ADMISSIONS_COMMITTEE}
              />
            ) : null}
            {selectedCategory?.category.goal === GoalName.CAREER &&
            hasProfessionalHiringExperience ? (
              <AdditionalExperienceTag type={ExperienceType.HIRING_MANAGER} />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default CoachInfo;
